import { Checkbox, RangeSlider } from "@clintonelec/react-storybook";
import { MotionMode, MotionObject } from "Data/Objects/Camera";
import { produce } from "immer";
import type { MotionCameraState } from "Interfaces";
import { type Dispatch, memo, type SetStateAction } from "react";

type SmartMotionControlsProps = {
	localMotionSettings: MotionCameraState;
	setLocalMotionSettings: Dispatch<SetStateAction<MotionCameraState>>;
};

function SmartMotionControls(props: SmartMotionControlsProps) {
	const {
		localMotionSettings,
		localMotionSettings: {
			motionMode, selectedMotionObjects, personConfidenceLevel, bikeConfidenceLevel, vehicleConfidenceLevel
		},
		setLocalMotionSettings
	} = props;

	const smartMotionDisabled = motionMode === MotionMode.VIDEO;
	const smartMotionValidation = selectedMotionObjects.length === 0
		? "smart-motion-validation"
		: "smart-motion-validation-disabled";

	const handlePersonConfidenceOnChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalMotionSettings(produce(localMotionSettings, draft => {
			draft.personConfidenceLevel = value;
		}));
	};

	const handleVehicleConfidenceOnChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalMotionSettings(produce(localMotionSettings, draft => {
			draft.vehicleConfidenceLevel = value;
		}));
	};

	const handleBikeConfidenceOnChange = (value: number | number[]) => {
		if (Array.isArray(value)) {
			return;
		}

		setLocalMotionSettings(produce(localMotionSettings, draft => {
			draft.bikeConfidenceLevel = value;
		}));
	};

	return (
		<div className="motion-setting-row smart-motion-settings-row">
			<span>Smart Motion Object</span>
			<div className="smart-motion-object-group">
				<div className="smart-motion-object">
					<Checkbox
						disabled={ smartMotionDisabled }
						name="personConfidence"
						checked={ selectedMotionObjects.includes(MotionObject.PERSON) }
					>
						<span>Person</span>
					</Checkbox>
					<RangeSlider
						hideInputCharMessage
						inputDisplay
						disabled={ smartMotionDisabled }
						min={ 0 }
						max={ 100 }
						onChange={ handlePersonConfidenceOnChange }
						value={ personConfidenceLevel }
					/>
				</div>
				<div className="smart-motion-object">
					<Checkbox
						disabled={ smartMotionDisabled }
						name="vehicleConfidence"
						checked={ selectedMotionObjects.includes(MotionObject.VEHICLE) }
					>
						<span>Vehicle</span>
					</Checkbox>
					<RangeSlider
						hideInputCharMessage
						inputDisplay
						disabled={ smartMotionDisabled }
						min={ 0 }
						max={ 100 }
						onChange={ handleVehicleConfidenceOnChange }
						value={ vehicleConfidenceLevel }
					/>
				</div>
				<div className="smart-motion-object">
					<Checkbox
						disabled={ smartMotionDisabled }
						name="bikeConfidence"
						checked={ selectedMotionObjects.includes(MotionObject.BIKE) }
					>
						<span>Bike</span>
					</Checkbox>
					<RangeSlider
						hideInputCharMessage
						inputDisplay
						disabled={ smartMotionDisabled }
						min={ 0 }
						max={ 100 }
						onChange={ handleBikeConfidenceOnChange }
						value={ bikeConfidenceLevel }
					/>
				</div>
				<span className={ smartMotionValidation }>
					At least one object must be selected.
				</span>
			</div>
		</div>
	);
}

export default memo(SmartMotionControls);
